/**
 * Process Monitor
 *
 * Displays long-running background processes in the status bar
 * Shows: process type, skill name, model, elapsed time, status
 */

import { useEffect, useState } from 'react';
import { useProcessMonitor } from '@/stores/processMonitorStore';
import { Loader2, CheckCircle, XCircle, Clock, ChevronDown, ChevronUp, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export const ProcessMonitor: React.FC = () => {
  const { activeProcesses, isExpanded, toggleExpanded, removeProcess } = useProcessMonitor();
  const [, forceUpdate] = useState(0);

  // Update elapsed time every second
  useEffect(() => {
    const interval = setInterval(() => {
      forceUpdate(n => n + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const activeArray = Array.from(activeProcesses.values());

  if (activeArray.length === 0) {
    return null;
  }

  const formatElapsedTime = (startedAt: number): string => {
    const elapsed = Date.now() - startedAt;
    const seconds = Math.floor(elapsed / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-3 h-3 text-green-500" />;
      case 'failed':
      case 'timeout':
        return <XCircle className="w-3 h-3 text-red-500" />;
      case 'processing':
        return <Loader2 className="w-3 h-3 text-blue-500 animate-spin" />;
      case 'queued':
        return <Clock className="w-3 h-3 text-yellow-500" />;
      default:
        return <Loader2 className="w-3 h-3 text-gray-500" />;
    }
  };

  const getStatusColor = (startedAt: number, status: string): string => {
    const elapsed = Date.now() - startedAt;
    const minutes = elapsed / 1000 / 60;

    if (status === 'completed') return 'text-green-600';
    if (status === 'failed' || status === 'timeout') return 'text-red-600';
    if (minutes > 2) return 'text-yellow-600'; // Taking longer than expected
    return 'text-blue-600';
  };

  return (
    <div className="flex items-center gap-2">
      {/* Collapsed View */}
      <button
        onClick={toggleExpanded}
        className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-accent text-xs"
      >
        <Loader2 className="w-3 h-3 text-blue-500 animate-spin" />
        <span className="text-muted-foreground">
          {activeArray.length} process{activeArray.length !== 1 ? 'es' : ''}
        </span>
        {isExpanded ? (
          <ChevronUp className="w-3 h-3 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        )}
      </button>

      {/* Expanded View - Dropdown */}
      {isExpanded && (
        <div className="absolute bottom-8 right-4 bg-background border border-border rounded-lg shadow-lg p-3 min-w-[300px] max-w-[400px] z-50">
          <div className="space-y-2">
            {activeArray.map((process) => (
              <div
                key={process.id}
                className="flex flex-col gap-1 p-2 rounded bg-accent/50"
              >
                {/* Header */}
                <div className="flex items-center gap-2">
                  {getStatusIcon(process.status)}
                  <span className="text-xs font-medium flex-1">
                    {process.type.toUpperCase()} Generation
                  </span>
                  <span className={cn('text-xs font-mono', getStatusColor(process.startedAt, process.status))}>
                    ⏱️ {formatElapsedTime(process.startedAt)}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeProcess(process.id);
                    }}
                    className="p-0.5 hover:bg-accent rounded"
                    title="Dismiss process"
                  >
                    <X className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                  </button>
                </div>

                {/* Skill Name */}
                <div className="text-xs text-muted-foreground pl-5">
                  {process.skillName}
                </div>

                {/* Model Info */}
                {process.modelInfo && (
                  <div className="text-xs text-muted-foreground pl-5 font-mono">
                    {process.modelInfo.actualModel || process.modelInfo.selectedModel}
                  </div>
                )}

                {/* Status */}
                <div className="text-xs text-muted-foreground pl-5">
                  {process.status === 'processing' && 'Processing...'}
                  {process.status === 'queued' && 'Queued...'}
                  {process.status === 'completing' && 'Finishing up...'}
                  {process.status === 'completed' && '✅ Completed'}
                  {process.status === 'failed' && `❌ Failed: ${process.error || 'Unknown error'}`}
                  {process.status === 'timeout' && '⏱️ Timed out'}
                </div>

                {process.warnings && process.warnings.length > 0 && (
                  <div className="text-xs text-amber-600 pl-5">
                    ⚠ {process.warnings.join(' • ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
